'use client'

import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react'
import './spotlight-cards.css'

/**
 * Cursor-following spotlight + border glow for cards — extracted from React Bits
 * "MagicBento" (border-glow / spotlight effect only, no bento grid, no gsap).
 *
 * Wrap a group of cards with <SpotlightCards> and give each card the
 * `spotlight-card` class. As the cursor moves over the section, each card's
 * border glows based on proximity, and a soft ambient circle trails the cursor.
 */

const DEFAULT_SPOTLIGHT_RADIUS = 300
const DEFAULT_GLOW_COLOR = '132, 0, 255'
const MOBILE_BREAKPOINT = 768

export default function SpotlightCards({
	children,
	className = '',
	spotlightRadius = DEFAULT_SPOTLIGHT_RADIUS,
	glowColor = DEFAULT_GLOW_COLOR,
	enabled = true,
}: {
	children: ReactNode
	className?: string
	spotlightRadius?: number
	glowColor?: string
	enabled?: boolean
}) {
	const sectionRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const section = sectionRef.current
		if (!section || !enabled) return

		// no hover glow on touch / small screens or when reduced motion is requested
		const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
		if (reduce || window.innerWidth <= MOBILE_BREAKPOINT) return

		const proximity = spotlightRadius * 0.5
		const fadeDistance = spotlightRadius * 0.75

		const spotlight = document.createElement('div')
		spotlight.className = 'card-spotlight'
		spotlight.style.cssText = `
			position: fixed;
			left: 0;
			top: 0;
			width: 800px;
			height: 800px;
			border-radius: 50%;
			pointer-events: none;
			opacity: 0;
			transform: translate(-50%, -50%);
			z-index: 30;
			transition: opacity 0.25s ease-out, left 0.12s ease-out, top 0.12s ease-out;
			background: radial-gradient(circle,
				rgba(${glowColor}, 0.15) 0%,
				rgba(${glowColor}, 0.08) 15%,
				rgba(${glowColor}, 0.04) 25%,
				rgba(${glowColor}, 0.02) 40%,
				rgba(${glowColor}, 0.01) 65%,
				transparent 70%
			);
		`
		document.body.appendChild(spotlight)

		const clearGlow = () => {
			spotlight.style.opacity = '0'
			section
				.querySelectorAll<HTMLElement>('.spotlight-card')
				.forEach(card => card.style.setProperty('--glow-intensity', '0'))
		}

		const handleMouseMove = (e: MouseEvent) => {
			const rect = section.getBoundingClientRect()
			const inside =
				e.clientX >= rect.left &&
				e.clientX <= rect.right &&
				e.clientY >= rect.top &&
				e.clientY <= rect.bottom

			if (!inside) {
				clearGlow()
				return
			}

			const cards = section.querySelectorAll<HTMLElement>('.spotlight-card')
			let minDistance = Infinity

			cards.forEach(card => {
				const r = card.getBoundingClientRect()
				const centerX = r.left + r.width / 2
				const centerY = r.top + r.height / 2
				const distance =
					Math.hypot(e.clientX - centerX, e.clientY - centerY) -
					Math.max(r.width, r.height) / 2
				const effective = Math.max(0, distance)
				minDistance = Math.min(minDistance, effective)

				let intensity = 0
				if (effective <= proximity) intensity = 1
				else if (effective <= fadeDistance)
					intensity = (fadeDistance - effective) / (fadeDistance - proximity)

				const relX = ((e.clientX - r.left) / r.width) * 100
				const relY = ((e.clientY - r.top) / r.height) * 100
				card.style.setProperty('--glow-x', `${relX}%`)
				card.style.setProperty('--glow-y', `${relY}%`)
				card.style.setProperty('--glow-intensity', `${intensity}`)
				card.style.setProperty('--glow-radius', `${spotlightRadius}px`)
			})

			spotlight.style.left = `${e.clientX}px`
			spotlight.style.top = `${e.clientY}px`

			const targetOpacity =
				minDistance <= proximity
					? 0.8
					: minDistance <= fadeDistance
						? ((fadeDistance - minDistance) / (fadeDistance - proximity)) * 0.8
						: 0
			spotlight.style.opacity = `${targetOpacity}`
		}

		document.addEventListener('mousemove', handleMouseMove)
		document.addEventListener('mouseleave', clearGlow)

		return () => {
			document.removeEventListener('mousemove', handleMouseMove)
			document.removeEventListener('mouseleave', clearGlow)
			spotlight.remove()
		}
	}, [enabled, spotlightRadius, glowColor])

	return (
		<div
			ref={sectionRef}
			className={`spotlight-section ${className}`}
			style={{ '--glow-rgb': glowColor } as CSSProperties}
		>
			{children}
		</div>
	)
}
